/**
 * Invite links (docs/directory-plan.md 10.4). Invites are created and claimed
 * only through the Cloud Functions; the app reads them and the super admin may
 * cancel one.
 */
import { useCallback, useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { inviteLink } from '../utils/superAdmin';
import type { MailStatus } from './useListingEmails';

export interface Invite {
  token: string;
  email: string;
  listingId: string;
  listingNameHe?: string;
  listingNameEn?: string;
  createdAt: string;
  expiresAt: string;
  used: boolean;
}

/** Firebase's error code without the "functions/" prefix, e.g. 'failed-precondition'. */
export function inviteErrorCode(err: unknown): string {
  const code = (err as { code?: string } | undefined)?.code ?? '';
  return code.replace(/^functions\//, '') || 'internal';
}

/** Open (unused) invites for one listing; super admin only. */
export function useListingInvites(listingId: string | undefined) {
  const [invites, setInvites] = useState<Invite[]>([]);

  useEffect(() => {
    if (!listingId) {
      setInvites([]);
      return;
    }
    const unsubscribe = onSnapshot(
      query(collection(db, 'invites'), where('listingId', '==', listingId)),
      (snapshot) =>
        setInvites(
          snapshot.docs
            .map((d) => ({ token: d.id, ...d.data() } as Invite))
            .filter((i) => !i.used)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        ),
      () => setInvites([])
    );
    return () => unsubscribe();
  }, [listingId]);

  return invites;
}

export function useInviteActions() {
  const [busy, setBusy] = useState(false);

  /** Returns the link to send, and with sendEmail whether the email went out. */
  const createInvite = useCallback(async (email: string, listingId: string, sendEmail = false) => {
    setBusy(true);
    try {
      const result = await httpsCallable<
        { email: string; listingId: string; sendEmail: boolean },
        { token: string; emailStatus: MailStatus | null }
      >(functions, 'createInvite')({ email: email.trim(), listingId, sendEmail });
      return { link: inviteLink(window.location.origin, result.data.token), emailStatus: result.data.emailStatus };
    } finally {
      setBusy(false);
    }
  }, []);

  const cancelInvite = useCallback(async (token: string) => {
    await deleteDoc(doc(db, 'invites', token));
  }, []);

  return { busy, createInvite, cancelInvite };
}

/** One invite, for the /join page. `invite` is null when the link is unknown. */
export function useInvite(token: string | undefined) {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'invites', token))
      .then((snap) => {
        if (!cancelled) setInvite(snap.exists() ? ({ token: snap.id, ...snap.data() } as Invite) : null);
      })
      .catch(() => {
        if (!cancelled) setInvite(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { invite, loading };
}

/**
 * Accepts an invite. Without being signed in, a password creates the new account;
 * signed in, the listing is added to the current account.
 */
export async function claimInvite(token: string, password?: string) {
  const result = await httpsCallable<{ token: string; password?: string }, { listingId: string; created: boolean }>(
    functions,
    'claimInvite'
  )(password ? { token, password } : { token });
  return result.data;
}
